import { useApi } from 'providers/ApiProvider'
import { Tag } from 'apis/entities/tag.entity'
import { useCallback, useEffect, useState } from 'react'
import Spinner from './Spinner'
import { StyleUtil } from 'utils/StyleUtil'
import { Role, useCompositeAuth } from 'providers/CompositeAuthProvider'
import { ToastUtil } from 'utils/ToastUtil'
import ButtonSpinner from './ButtonSpinner'

export default function ManageImages() {
  const { getRole } = useCompositeAuth()
  const isSuperAdmin = getRole() === Role.SuperAdmin
  const canDeleteSubjects = isSuperAdmin
  const { getTags, updateTag, deleteTag } = useApi()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [mapLoadingSave, setMapLoadingSave] = useState<Map<string, boolean>>(
    new Map(),
  )
  const [mapLoadingDelete, setMapLoadingDelete] = useState<
    Map<string, boolean>
  >(new Map())

  const [tags, setTags] = useState<Tag[]>([])
  const [confirmDeleteTagId, setConfirmDeleteTagId] = useState<string | null>(
    null,
  )

  const fetchTags = useCallback(async () => {
    setIsLoading(true)
    const data = await getTags()
    setIsLoading(false)
    setTags(data)
  }, [getTags])

  const onSave = async (tagId: string) => {
    const input = document.getElementById(
      `tag-input-${tagId}`,
    ) as HTMLInputElement
    const value = input.value
    if (value) {
      try {
        setMapLoadingSave((prev) => new Map(prev.set(tagId, true)))
        await updateTag(tagId, value)
        ToastUtil.success('Changed successfully')
      } catch (error) {
        ToastUtil.error('Change failed')
      } finally {
        setMapLoadingSave((prev) => new Map(prev.set(tagId, false)))
      }
    } else {
      ToastUtil.error('Tag name cannot be empty')
    }
  }

  const onDelete = (tagId: string) => {
    setConfirmDeleteTagId(tagId)
  }

  const onCancelDelete = () => {
    setConfirmDeleteTagId(null)
  }

  const onConfirmDelete = async (tagId: string) => {
    try {
      setMapLoadingDelete(new Map(mapLoadingDelete.set(tagId, true)))
      const result = await deleteTag(tagId)

      if (result) {
        ToastUtil.success('Deleted successfully')
        await fetchTags()
      } else {
        ToastUtil.error('Deletion failed')
      }
    } catch (error) {
      ToastUtil.error('Deletion failed')
    } finally {
      setMapLoadingDelete(new Map(mapLoadingDelete.set(tagId, false)))
      setConfirmDeleteTagId(null)
    }
  }

  useEffect(() => {
    fetchTags()
  }, [fetchTags])

  return (
    <>
      {isLoading && <Spinner />}
      <div className="flex items-center justify-center">
        <ul data-testid="list-tags" className="mt-8 w-full">
          {tags &&
            tags.map((tag, index) => {
              return (
                <li
                  data-testid={`li-${tag.name}`}
                  key={tag.id}
                  className="flex flex-row items-center justify-center gap-6"
                >
                  <span className="mr-[-12px] w-[10px] text-right font-roboto text-black">
                    {index + 1}
                  </span>
                  <input
                    data-testid="input-tag"
                    className="m-3 mr-0 w-1/2 border border-[#C8C8C8] px-[12px] py-[5px] font-roboto"
                    type="text"
                    id={`tag-input-${tag.id}`}
                    defaultValue={tag.name}
                  />
                  <button
                    data-testid="btn-save-tag"
                    onClick={() => onSave(tag.id)}
                    className={StyleUtil.buttonBlackWhite}
                    disabled={mapLoadingSave.get(tag.id)}
                    style={{ width: '80px' }}
                  >
                    {mapLoadingSave.get(tag.id) ? <ButtonSpinner /> : 'Rename'}
                  </button>
                  {canDeleteSubjects &&
                    (confirmDeleteTagId !== tag.id ? (
                      <button
                        data-testid="btn-delete-tag"
                        onClick={() => onDelete(tag.id)}
                        className={`${StyleUtil.buttonPrimaryUnsw} w-[120px]`}
                      >
                        Delete
                      </button>
                    ) : (
                      <div className="mr-[-123px] flex flex-row items-center justify-center gap-3">
                        <span className="text-[14px]">Are you sure?</span>
                        <button
                          data-testid="btn-confirm-delete-tag"
                          onClick={() => onConfirmDelete(tag.id)}
                          className={StyleUtil.buttonPrimaryUnsw}
                          disabled={mapLoadingDelete.get(tag.id)}
                          style={{ width: '50px' }}
                        >
                          {mapLoadingDelete.get(tag.id) ? (
                            <ButtonSpinner />
                          ) : (
                            'Yes'
                          )}
                        </button>
                        <span
                          data-testid="btn-cancel-delete-tag"
                          onClick={onCancelDelete}
                          className="cursor-pointer text-[14px] text-secondaryIndigo underline hover:opacity-[0.8]"
                        >
                          Cancel
                        </span>
                      </div>
                    ))}
                </li>
              )
            })}
        </ul>
      </div>
    </>
  )
}
